/**
 * Dashboard - Hovedside for AdvokatCRM
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import FakturaRegistrering from '../components/FakturaRegistrering';
import EpostInnboks from '../components/EpostInnboks';
import DashboardTodos from '../components/DashboardTodos';
import OpprettFristModal from '../components/OpprettFristModal';
import './Dashboard.css';

export default function Dashboard({ navigerTil }) {
  const [stats, setStats] = useState({
    aktiveSaker: 0,
    komendeFrister: 0,
    nyeEposter: 0,
    tidsregistrering: 0,
    totaltKlienter: 0,
    sakUtenOppfolging: 0,
    forfalteFrister: 0,
    oppgaverIkkeStartet: 0
  });
  const [komendeFrister, setKomendeFrister] = useState([]);
  const [forfalteFrister, setForfalteFrister] = useState([]);
  const [sisteSaker, setSisteSaker] = useState([]);
  const [budsjettData, setBudsjettData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visFakturaModal, setVisFakturaModal] = useState(false);
  const [visFristModal, setVisFristModal] = useState(false);
  const [synkronisererMicrosoft, setSynkronisererMicrosoft] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Hent ALLE aktive saker for å få riktig telling (høy limit for å få alle)
      const alleSakerResponse = await crmApi.getSaker({ status: 'aktiv', limit: 10000 });
      const alleSaker = alleSakerResponse.saker || [];

      // Hent de 5 siste for visning
      const sisteSakerResponse = await crmApi.getSaker({ status: 'aktiv', limit: 5 });
      const sisteSaker = sisteSakerResponse.saker || [];

      // Hent kommende frister (neste 7 dager)
      const fristerResponse = await crmApi.getFrister();
      const frister = fristerResponse.frister || [];
      const now = new Date();
      const next7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

      const kommende = frister.filter(f => {
        const fristDato = new Date(f.frist_dato);
        return fristDato >= now && fristDato <= next7Days && f.status === 'aktiv';
      });

      const forfalte = frister.filter(f => {
        const fristDato = new Date(f.frist_dato);
        return fristDato < now && f.status === 'aktiv';
      });

      // Hent klienter
      let totaltKlienter = 0;
      try {
        const klienterResponse = await crmApi.getKlienter();
        totaltKlienter = (klienterResponse.klienter || []).length;
      } catch (error) {
        console.error('Kunne ikke hente klienter:', error);
      }

      // Hent oppgaver
      let oppgaverIkkeStartet = 0;
      try {
        const oppgaverResponse = await crmApi.getOppgaver();
        const oppgaver = oppgaverResponse.oppgaver || [];
        oppgaverIkkeStartet = oppgaver.filter(o => o.status === 'ikke_startet').length;
      } catch (error) {
        console.error('Kunne ikke hente oppgaver:', error);
      }

      // Tell saker uten oppfølging
      const sakUtenOppfolging = alleSaker.filter(s =>
        !s.neste_oppfolging_klient_dato && !s.neste_oppfolging_motpart_dato
      ).length;

      // Hent tidsregistrering denne uken
      let timerDenneUken = 0;
      try {
        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Mandag
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Søndag
        endOfWeek.setHours(23, 59, 59, 999);

        const timeStatsResponse = await fetch(`${crmApi.baseUrl}/api/time/stats?startDate=${startOfWeek.getTime()}&endDate=${endOfWeek.getTime()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        if (timeStatsResponse.ok) {
          const timeStats = await timeStatsResponse.json();
          timerDenneUken = Math.round((timeStats.total_minutes || 0) / 60 * 10) / 10; // Rund av til 1 desimal
        }
      } catch (error) {
        console.error('Kunne ikke hente tidsstatistikk:', error);
      }

      setStats({
        aktiveSaker: alleSaker.length || 0,
        komendeFrister: kommende.length || 0,
        nyeEposter: 0, // TODO: Implementer e-post-telling
        tidsregistrering: timerDenneUken,
        totaltKlienter,
        sakUtenOppfolging,
        forfalteFrister: forfalte.length,
        oppgaverIkkeStartet
      });

      setKomendeFrister(kommende.slice(0, 5));
      setForfalteFrister(forfalte.slice(0, 10)); // Vis maks 10 forfalte
      setSisteSaker(sisteSaker);

      // Hent budsjett-varsling data
      try {
        const budsjettResponse = await crmApi.getBudsjettDashboardWidget();
        setBudsjettData(budsjettResponse);
      } catch (error) {
        // Budsjett-endepunkt ikke implementert enda - stille feil
        setBudsjettData(null);
      }

    } catch (error) {
      console.error('Feil ved lasting av dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromMicrosoft = async () => {
    try {
      setSynkronisererMicrosoft(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/oppgaver/sync-from-microsoft`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Kunne ikke synkronisere fra Microsoft');
      }

      const result = await response.json();
      alert(`✅ Synkronisert ${result.oppdaterte || 0} oppgaver fra Microsoft To Do`);

      // Reload dashboard for å vise oppdaterte oppgaver
      loadDashboardData();
    } catch (error) {
      console.error('Feil ved synkronisering:', error);
      alert('Kunne ikke synkronisere fra Microsoft: ' + error.message);
    } finally {
      setSynkronisererMicrosoft(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard loading">
        <div className="spinner"></div>
        <p>Laster dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>📊 Dashboard</h1>
        <div className="dashboard-actions">
          <p>Velkommen til AdvokatCRM</p>
          <button
            className="btn-primary"
            onClick={() => setVisFakturaModal(true)}
          >
            📄 Registrer faktura
          </button>
          <button
            className="btn-rettshjelp"
            onClick={() => navigerTil && navigerTil('rettshjelp')}
          >
            🛡️ Oversikt rettshjelp
          </button>
          <button
            className="btn-secondary"
            onClick={handleSyncFromMicrosoft}
            disabled={synkronisererMicrosoft}
            title="Synkroniser oppgaver fra Microsoft To Do"
          >
            {synkronisererMicrosoft ? '⏳ Synkroniserer...' : '🔄 Sync fra Microsoft'}
          </button>
        </div>
      </div>

      {/* Statistikk-kort */}
      <div className="stats-grid">
        <div className="stat-card" onClick={() => navigerTil && navigerTil('saker')} style={{cursor: 'pointer'}}>
          <div className="stat-icon">📁</div>
          <div className="stat-content">
            <h3>{stats.aktiveSaker}</h3>
            <p>Aktive saker</p>
          </div>
        </div>

        <div className="stat-card warning" onClick={() => navigerTil && navigerTil('frister')} style={{cursor: 'pointer'}}>
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <h3>{stats.komendeFrister}</h3>
            <p>Kommende frister (7 dager)</p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigerTil && navigerTil('klienter')} style={{cursor: 'pointer'}}>
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>{stats.totaltKlienter}</h3>
            <p>Totalt klienter</p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigerTil && navigerTil('oppgaver')} style={{cursor: 'pointer'}}>
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <h3>{stats.oppgaverIkkeStartet}</h3>
            <p>Oppgaver ikke startet</p>
          </div>
        </div>

        {stats.forfalteFrister > 0 && (
          <div className="stat-card danger" onClick={() => navigerTil && navigerTil('frister')} style={{cursor: 'pointer'}}>
            <div className="stat-icon">🚨</div>
            <div className="stat-content">
              <h3>{stats.forfalteFrister}</h3>
              <p>Forfalte frister!</p>
            </div>
          </div>
        )}

        {stats.sakUtenOppfolging > 0 && (
          <div className="stat-card warning" onClick={() => navigerTil && navigerTil('saker')} style={{cursor: 'pointer'}}>
            <div className="stat-icon">⚠️</div>
            <div className="stat-content">
              <h3>{stats.sakUtenOppfolging}</h3>
              <p>Saker uten oppfølging</p>
            </div>
          </div>
        )}
      </div>

      {/* Budsjett-varsling */}
      {budsjettData && (budsjettData.antall_kritisk > 0 || budsjettData.antall_varsling > 0) && (
        <div className="budsjett-varsling">
          <div className="varsling-header">
            <h3>⚠️ Budsjettvarsel</h3>
            <span className="varsling-summary">
              {budsjettData.antall_kritisk > 0 && (
                <span className="kritisk-badge">🔴 {budsjettData.antall_kritisk} kritisk</span>
              )}
              {budsjettData.antall_varsling > 0 && (
                <span className="varsling-badge">🟡 {budsjettData.antall_varsling} varsel</span>
              )}
            </span>
          </div>

          {budsjettData.kritiske_saker && budsjettData.kritiske_saker.length > 0 && (
            <div className="kritiske-saker-list">
              {budsjettData.kritiske_saker.map((sak, idx) => (
                <div key={idx} className="budsjett-sak-item kritisk">
                  <div className="sak-info">
                    <strong>{sak.saksnummer}</strong>
                    <span>{sak.klient_navn}</span>
                  </div>
                  <div className="budsjett-bar">
                    <div
                      className="budsjett-progress kritisk-progress"
                      style={{ width: `${Math.min(sak.budsjett_prosent, 100)}%` }}
                    ></div>
                  </div>
                  <div className="budsjett-prosent kritisk-prosent">
                    {sak.budsjett_prosent}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="dashboard-content">
        {/* Kommende frister */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>⏰ Frister</h2>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button
                className="btn-primary"
                onClick={() => setVisFristModal(true)}
                style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
              >
                📅 Ny frist
              </button>
              <a href="/frister" className="view-all">Se alle →</a>
            </div>
          </div>

          {(() => {
            if (komendeFrister.length === 0 && forfalteFrister.length === 0) {
              return (
                <div className="empty-state">
                  <p>Ingen frister 🎉</p>
                </div>
              );
            }

            return (
              <>
                {/* Forfalte frister */}
                {forfalteFrister.length > 0 && (
                  <div style={{marginBottom: '20px'}}>
                    <h4 style={{fontSize: '0.9rem', color: '#991b1b', marginBottom: '8px', fontWeight: 600}}>
                      🚨 Forfalte frister ({forfalteFrister.length})
                    </h4>
                    <div className="frister-list">
                      {forfalteFrister.map(frist => (
                        <div key={frist.id} className="frist-item" style={{backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444'}}>
                          <div className="frist-date" style={{color: '#ef4444', fontWeight: 'bold'}}>
                            {formatDato(frist.frist_dato)}
                          </div>
                          <div className="frist-content">
                            <h4 style={{color: '#991b1b'}}>⚠️ {frist.beskrivelse}</h4>
                            <p>Sak: {frist.saksnummer}</p>
                          </div>
                          <div className={`frist-priority priority-${frist.prioritet}`} style={{backgroundColor: '#ef4444', color: 'white'}}>
                            {frist.prioritet}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Kommende frister */}
                {komendeFrister.length > 0 && (
                  <div>
                    <h4 style={{fontSize: '0.9rem', color: '#6b7280', marginBottom: '8px', fontWeight: 600}}>
                      📅 Kommende frister (7 dager) ({komendeFrister.length})
                    </h4>
                    <div className="frister-list">
                      {komendeFrister.map(frist => (
                        <div key={frist.id} className="frist-item">
                          <div className="frist-date">
                            {formatDato(frist.frist_dato)}
                          </div>
                          <div className="frist-content">
                            <h4>{frist.beskrivelse}</h4>
                            <p>Sak: {frist.saksnummer}</p>
                          </div>
                          <div className={`frist-priority priority-${frist.prioritet}`}>
                            {frist.prioritet}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* E-post innboks */}
        <div className="dashboard-section">
          <EpostInnboks />
        </div>

        {/* Gjøremålsliste */}
        <div className="dashboard-section">
          <DashboardTodos />
        </div>

        {/* Siste saker */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>📁 Siste saker</h2>
            <a href="/saker" className="view-all">Se alle →</a>
          </div>

          {sisteSaker.length === 0 ? (
            <div className="empty-state">
              <p>Ingen saker ennå</p>
              <button className="btn-primary" onClick={() => window.location.href = '/saker/ny'}>
                Opprett første sak
              </button>
            </div>
          ) : (
            <div className="saker-list">
              {sisteSaker.map(sak => (
                <div
                  key={sak.id}
                  className="sak-item"
                  onClick={() => window.location.href = `/saker/${sak.id}`}
                >
                  <div className="sak-header">
                    <h4>{sak.saksnummer} - {sak.tittel}</h4>
                    <span className={`status-badge status-${sak.status}`}>
                      {sak.status}
                    </span>
                  </div>
                  <p className="sak-klient">Klient: {sak.klient_navn}</p>
                  <p className="sak-type">{sak.sakstype}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fakturaregistrering modal */}
      {visFakturaModal && (
        <FakturaRegistrering
          onClose={() => setVisFakturaModal(false)}
          onSuccess={(fakturaId) => {
            setVisFakturaModal(false);
            loadDashboardData(); // Oppdater dashboard
          }}
        />
      )}

      {/* Opprett frist modal */}
      {visFristModal && (
        <OpprettFristModal
          onClose={() => setVisFristModal(false)}
          onSuccess={() => {
            setVisFristModal(false);
            loadDashboardData(); // Oppdater dashboard med nye frister
          }}
        />
      )}
    </div>
  );
}

function formatDato(datoString) {
  const dato = new Date(datoString);
  const now = new Date();
  const diff = Math.floor((dato - now) / (1000 * 60 * 60 * 24));

  if (diff === 0) return 'I dag';
  if (diff === 1) return 'I morgen';
  if (diff > 1 && diff < 7) return `Om ${diff} dager`;

  return dato.toLocaleDateString('nb-NO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
